import React, { useCallback } from "react";
import { useFormikContext } from "formik";
import { Flex, Text } from "@jsq/ds/core";
import { FormTextInput, FormDatePicker } from "@jsq/ds/forms";
import { PayableFormFields, type ClientStepFormValues } from "../../types";

/**
 * VendorInvoiceDetails component for vendor payee invoice information.
 *
 * This component uses useFormikContext to access the current invoice date value
 * for real-time filtering of the due date picker and conditional disabling.
 */
export const VendorInvoiceDetails: React.FC = () => {
  const { values } = useFormikContext<ClientStepFormValues>();

  const invoiceDate = values[PayableFormFields.InvoiceDate];

  // Filter due date to be on or after invoice date
  const filterDueDate = useCallback(
    (date: Date) => {
      if (!invoiceDate) return true;
      const invoiceDateNormalized = new Date(invoiceDate);
      invoiceDateNormalized.setHours(0, 0, 0, 0);
      const dateNormalized = new Date(date);
      dateNormalized.setHours(0, 0, 0, 0);
      return dateNormalized >= invoiceDateNormalized;
    },
    [invoiceDate],
  );

  // Due date should be disabled until invoice date is selected
  const isDueDateDisabled = !invoiceDate;

  return (
    <Flex flexDirection="column" gap={20}>
      <Flex flexDirection="column" gap={8}>
        <Text>Invoice number</Text>
        <FormTextInput
          name={PayableFormFields.InvoiceNumber}
          placeholder="Enter invoice number"
          isFullWidth
          whitespace="trim"
        />
      </Flex>

      <Flex flexDirection="column" gap={8}>
        <Text>Invoice date</Text>
        <FormDatePicker
          name={PayableFormFields.InvoiceDate}
          placeholderText="Select invoice date"
          dateFormat="MMM d, yyyy"
          hasIcon
          isFull
        />
      </Flex>

      <Flex flexDirection="column" gap={8}>
        <Text>Due date</Text>
        <FormDatePicker
          name={PayableFormFields.DueDate}
          placeholderText="Select due date"
          dateFormat="MMM d, yyyy"
          hasIcon
          isFull
          filterDate={filterDueDate}
          disabled={isDueDateDisabled}
        />
      </Flex>

      <Flex flexDirection="column" gap={8}>
        <Text>Invoice description</Text>
        <FormTextInput
          name={PayableFormFields.Description}
          placeholder="Enter description"
          isFullWidth
          maxLength={250}
        />
      </Flex>
    </Flex>
  );
};

export default VendorInvoiceDetails;
